/**
 * Video Utilities
 * 
 * Helper functions for video URL handling based on user subscription status
 */

import type { Asset } from '../services/assets';
import type { User } from '../types/api';

/**
 * Determine which video URL to use based on user's plan status
 * Free users see watermarked version, paid users see original
 * 
 * @param asset - The asset containing video URLs
 * @param user - The user object with subscription information (can be null for anonymous users)
 * @returns The appropriate video URL to display
 */
export function getVideoUrl(asset: Asset, user: User | null): string | undefined {
  // Determine if user is on free plan
  // User is considered free if:
  // - No user object (anonymous)
  // - No subscription_status or subscription_status is 'free'
  // - subscription_status is not 'active' or 'canceling' (which means paid)
  const isFreeUser = !user || 
                     !user.subscription_status || 
                     user.subscription_status === 'free' ||
                     (user.subscription_status !== 'active' && user.subscription_status !== 'canceling');
  
  if (isFreeUser) {
    // Free users see watermarked version (fallback to unwatermarked if not available)
    return asset.watermarked_video_url || asset.video_url;
  } else {
    // Paid users see original unwatermarked version
    return asset.video_url;
  }
}

/**
 * Check if a user has an active paid subscription
 * 
 * @param user - The user object with subscription information
 * @returns True if user has active paid subscription
 */
export function hasActivePaidSubscription(user: User | null): boolean {
  return !!(user?.subscription_status && 
    (user.subscription_status === 'active' || user.subscription_status === 'canceling'));
}
